import { type ScaleLinear, axisTop, scaleLinear, select } from "d3";
import { cloneDeep, defaultsDeep } from "lodash-es";
import { Component, type ReactElement, createRef } from "react";

import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { type IBoxModel, type ITrackBoxModel } from "../genome-viewer/interfaces.js";
import ColorScaleLegend from "./ColorScaleLegend.js";
import { hitPileupParams } from "./HitHspChainShape.js";
import { HitsTrack } from "./HitsTrack.js";
import { RAINBOW_COLORS } from "@/app-utils/color.ts";
import { type IPileup, PileupFactory } from "@/app-utils/track-shapes/track-pileup-factory.ts";
import { type BlastHit, type BlastHsp, type Range, type Sequence } from "@/graphql.ts";
import {
  AXIS_HEIGHT,
  AXIS_TICK_COUNT,
  COLOR_SCALE_MAX_BIT_SCORE,
  COLOR_SCALE_MIN_BIT_SCORE,
} from "@/settings/settings.js";

export interface IQueryAnchoredHitsVisProps {
  boxModel: ITrackBoxModel; // basic width and padding info
  hits: BlastHit[];
  query: Sequence;
  onHitClick: (hit: BlastHit, hsp: BlastHsp) => void;
  showLegend?: boolean;
}

interface IQueryAnchoredHitsVisState {
  showLegend: boolean;
  scale: ScaleLinear<number, number>;
  axis: any;
}

/**
 * A visualization component that shows the query anchored alignments of all BLAST hits
 *
 * The component piles the hits of the provided BLAST search along a coordinate system representing the query
 * sequence. Each hit is represented by possibly multiple HSPs which are colored according to the bit score of the
 * HSP. Clicking on a HSP will trigger the onHitClick callback (which changes the location to the alignment of the
 * hit that was clicked).
 *
 * The actual visualization is broken down in two main components: the ColorScaleLegend shows a gradient color
 * scale legend for the bit scores, whereas the HitsTrack component takes care of the actual drawing of the HitHspChainShapes.
 *
 * created on 4/13/2016
 */
class QueryAnchoredHitsVis extends Component<IQueryAnchoredHitsVisProps, IQueryAnchoredHitsVisState> {
  public static defaultBoxModelProps: IBoxModel = {
    padding: {
      top: 10,
      bottom: 10,
      left: 30,
      right: 30,
    },
  };

  private axisRef = createRef<SVGGElement>();

  constructor(props: IQueryAnchoredHitsVisProps) {
    super(props);

    const scaleAndAxis: Partial<IQueryAnchoredHitsVisState> = this.calculateScaleAndAxis();
    this.state = {
      showLegend: props.showLegend,
      scale: scaleAndAxis.scale,
      axis: scaleAndAxis.axis,
    };
  }

  componentDidMount() {
    // the axis renders from scratch with mount
    this.renderAxis();
  }

  componentDidUpdate(prevProps: IQueryAnchoredHitsVisProps, _prevState: IQueryAnchoredHitsVisState) {
    if (this.props.boxModel !== prevProps.boxModel || this.props.query !== prevProps.query) {
      const scaleAndAxis: Partial<IQueryAnchoredHitsVisState> = this.calculateScaleAndAxis();
      this.setState({ scale: scaleAndAxis.scale, axis: scaleAndAxis.axis });
    }

    // the axis renders from scratch with every update
    this.renderAxis();
  }

  calculateScaleAndAxis = (): Partial<IQueryAnchoredHitsVisState> => {
    const boxModel: IBoxModel = this.props.boxModel;
    const canvasWidth: number = boxModel.width - boxModel.padding.left - boxModel.padding.right;

    const scale = scaleLinear()
      .domain([0, this.props.query?.length || 1])
      .range([0, canvasWidth]);

    // create the ticks on the scaled axis
    const axis = axisTop(scale)
      .ticks(AXIS_TICK_COUNT)
      .tickFormat((d) => {
        return d === 0 ? "" : d + "";
      });

    return { scale, axis };
  };

  renderAxis = () => {
    // axisRef is a reference to the existing, empty axis container
    // add the axis into this container
    select(this.axisRef.current).call(this.state.axis);
  };

  toggleLegend = () => {
    this.setState({ showLegend: !this.state.showLegend });
  };

  render() {
    const props: IQueryAnchoredHitsVisProps = this.props;

    if (!props.query) {
      return (
        <ContentPlaceholder
          text={"We can't show a Graphic Summary because the query sequence is not available anymore."}
        />
      );
    }

    const boxModel: IBoxModel = props.boxModel;
    const visibleQueryRange: Range = { start: 1, stop: props.query.length };

    // Compute pileup lanes before rendering the full component to calculate the required SVG height.
    const pileup: IPileup = new PileupFactory(this.state.scale).buildHitPileup(
      this.props.hits,
      visibleQueryRange,
      hitPileupParams,
    );
    const svgHeight: number = this.getSvgHeight(pileup);
    const contentWidth: number = boxModel.width - boxModel.padding.left - boxModel.padding.right;
    const legendHeight: number = this.getLegendHeight();

    // Create a box model for the color scale with the same left right padding as out input box model
    const colorScaleBoxModel: IBoxModel = this.createColorScaleBoxModel();

    // The ticklines start right under the axis and span the full SVG height.
    // Note that the small ticks above the axis are created by d3!
    const renderedTicklines: ReactElement[] = this.renderTicklines(visibleQueryRange, svgHeight);

    // All contents of the SVG is moved to the right in order to provide space for padding and the labels ("Genes"..."customize")
    const translateTicklines = `translate(${boxModel.padding.left}, 0)`;
    // Translate main content down to account for axis height (-1 is just a correction)
    const translateAxis = `translate(0, ${legendHeight + AXIS_HEIGHT - 1})`;
    // Translate the label to account for legend height
    const translateLegendToggle = `translate(${contentWidth}, 15)`;
    // Translate the label to account for legend height
    const translateLabel = `translate(0, ${legendHeight - 3})`;
    // Move hits track down to account for legend height and axis height
    const translateHitsTrack = `translate(0,${legendHeight + AXIS_HEIGHT})`;

    if (!pileup) {
      return;
    }
    return (
      <div
        style={{
          paddingTop: boxModel.padding.top,
          paddingBottom: boxModel.padding.bottom,
          height: "100%",
          overflow: "auto",
        }}
      >
        <svg width={boxModel.width} height={svgHeight} className="feature-tracklist">
          {this.state.showLegend && (
            <ColorScaleLegend
              title="Score"
              boxModel={colorScaleBoxModel}
              colors={RAINBOW_COLORS}
              domain={[COLOR_SCALE_MIN_BIT_SCORE, COLOR_SCALE_MAX_BIT_SCORE]}
              tickCount={5}
            />
          )}

          <g transform={translateTicklines}>
            <text className="label-text" textAnchor="start" transform={translateLabel}>
              Query Sequence
            </text>
            <text
              className="link-button"
              textAnchor="end"
              transform={translateLegendToggle}
              onClick={this.toggleLegend}
            >
              {this.state.showLegend ? "Hide Legend" : "Show Legend"}
            </text>
            {/* provide container for the axis and keep a global ref to it for insertion of the axis */}
            <g className="coordinates-track axis" transform={translateAxis} ref={this.axisRef} />
            {/* add the tick lines */}
            <g className="feature-track-ticklines">{renderedTicklines}</g>
          </g>

          {/* this is the main content in form of the rendered tracks */}
          <g transform={translateHitsTrack}>
            <HitsTrack
              boxModel={boxModel}
              range={visibleQueryRange}
              pileup={pileup}
              onHitClick={this.props.onHitClick}
            />
          </g>
        </svg>
      </div>
    );
  }

  private createColorScaleBoxModel(): IBoxModel {
    const colorScaleBoxModel: IBoxModel = cloneDeep(this.props.boxModel);
    colorScaleBoxModel.height = this.getLegendHeight();
    colorScaleBoxModel.padding.top = 0;
    colorScaleBoxModel.padding.bottom = 30;

    return colorScaleBoxModel;
  }

  private renderTicklines(visibleRange: Range, height: number): ReactElement[] {
    const ticks: number[] = this.state.axis.scale().ticks(this.state.axis.ticks()[0]);
    const ticksWithStartStop: number[] = ticks.concat([visibleRange.stop]);

    return ticksWithStartStop.map((tick: number, index: number) => {
      const x: number = this.state.scale(tick) + 0.5;
      return (
        <line
          className="tickline"
          x1={x}
          y1={AXIS_HEIGHT + this.getLegendHeight()}
          x2={x}
          y2={height}
          key={`tickline-${tick}-${index}`}
        />
      );
    });
  }

  private getLegendHeight(): number {
    return this.state.showLegend ? 50 : 20;
  }

  private getSvgHeight(pileup: IPileup): number {
    const bm: IBoxModel = this.props.boxModel;
    const { lanes, pileupParams } = pileup;
    const { featureHeight, labelHeight, lanePadding } = pileupParams;

    const pileupHeight = Math.max(lanes, 1) * (featureHeight + labelHeight) + (lanes - 1) * lanePadding;
    return this.getLegendHeight() + AXIS_HEIGHT + pileupHeight + bm.padding.top + bm.padding.bottom;
  }

  public static getPropsWithDefaultBoxModel(
    propsWithoutDefaults: IQueryAnchoredHitsVisProps,
  ): IQueryAnchoredHitsVisProps {
    propsWithoutDefaults.boxModel = defaultsDeep(
      propsWithoutDefaults.boxModel,
      QueryAnchoredHitsVis.defaultBoxModelProps,
    );
    return propsWithoutDefaults;
  }
}

export { QueryAnchoredHitsVis };
